import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  MEAL_TYPES,
  mealTypeLabel,
  useAnalyzePhoto,
  useAnalyzeText,
  useLogMeal,
  MealAnalysisInvalidError,
  MealAnalysisVisionUnavailableError,
  type LogMealRequest,
  type MealAnalysis,
  type MealAnalysisMediaType,
  type MealType,
} from '@/lib/nutrition';

/**
 * M2.34.D — Modal de añadido rápido de comida con análisis IA.
 *
 * Dos tabs:
 *   - "📷 Foto": el usuario elige una imagen (en móvil se abre la cámara
 *     trasera vía `capture="environment"`). Se previsualiza y se envía a
 *     `/log/analyze-photo`.
 *   - "✏️ Texto": textarea de hasta 500 caracteres, se envía a
 *     `/log/analyze-text`.
 *
 * Tras el análisis se renderiza un form editable con título, items y macros;
 * el usuario ajusta lo que quiera y pulsa "Guardar en log" → POST /log
 * (mismo endpoint que el flow manual). La invalidación del query del log
 * se delega en `useLogMeal`, así el caller solo necesita cerrar el modal en
 * `onSuccess`.
 *
 * Decisiones:
 *   - Mantenemos `useLogMeal` (el hook ya existente) en vez de un endpoint
 *     "save analysis" en backend — separar análisis de persistencia
 *     simplifica el contrato y permite al usuario editar libremente antes
 *     de guardar (el contrato del PRD pide explícitamente esa revisión).
 *   - Si `confidence < 0.5` mostramos un aviso amarillo invitando al
 *     usuario a revisar los campos. NO bloqueamos el guardado — el usuario
 *     es soberano y siempre puede corregir.
 *   - El tipo de comida y la fecha/hora se infieren del momento actual; el
 *     usuario los puede cambiar en el form editable.
 */
export function MealLogQuickAdd({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}): ReactElement | null {
  if (!open) return null;
  return (
    <Modal title="Añadir comida rápida" onClose={onClose}>
      <QuickAddBody onClose={onClose} onSuccess={onSuccess} />
    </Modal>
  );
}

type Tab = 'photo' | 'text';

function QuickAddBody({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess?: () => void;
}): ReactElement {
  const [tab, setTab] = useState<Tab>('photo');
  const [analysis, setAnalysis] = useState<MealAnalysis | null>(null);

  function handleAnalyzed(a: MealAnalysis) {
    setAnalysis(a);
  }

  function handleReset() {
    setAnalysis(null);
  }

  return (
    <div className="space-y-4">
      {!analysis && (
        <div role="tablist" aria-label="Modo de captura" className="flex gap-1 rounded-md border border-slate-200 bg-slate-50 p-1">
          <TabButton active={tab === 'photo'} onClick={() => setTab('photo')} controls="quickadd-photo">
            📷 Foto
          </TabButton>
          <TabButton active={tab === 'text'} onClick={() => setTab('text')} controls="quickadd-text">
            ✏️ Texto
          </TabButton>
        </div>
      )}

      {!analysis && tab === 'photo' && (
        <div id="quickadd-photo" role="tabpanel">
          <PhotoCapture onAnalyzed={handleAnalyzed} />
        </div>
      )}
      {!analysis && tab === 'text' && (
        <div id="quickadd-text" role="tabpanel">
          <TextCapture onAnalyzed={handleAnalyzed} />
        </div>
      )}

      {analysis && (
        <AnalysisReview
          analysis={analysis}
          onBack={handleReset}
          onSaved={() => {
            onSuccess?.();
            onClose();
          }}
          onCancel={onClose}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab buttons
// ---------------------------------------------------------------------------

function TabButton({
  active,
  onClick,
  controls,
  children,
}: {
  active: boolean;
  onClick: () => void;
  controls: string;
  children: ReactNode;
}): ReactElement {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      aria-controls={controls}
      onClick={onClick}
      className={
        active
          ? 'flex-1 rounded bg-white px-3 py-2 text-sm font-semibold text-teal-700 shadow-sm'
          : 'flex-1 rounded px-3 py-2 text-sm text-slate-600 hover:bg-white/60'
      }
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Photo capture
// ---------------------------------------------------------------------------

function PhotoCapture({
  onAnalyzed,
}: {
  onAnalyzed: (a: MealAnalysis) => void;
}): ReactElement {
  const mutation = useAnalyzePhoto();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [base64, setBase64] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<MealAnalysisMediaType | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const fileInputId = useId();

  // Revoca el object URL del preview al desmontar para no fugar memoria.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    setLocalError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed: MealAnalysisMediaType[] = [
      'image/jpeg',
      'image/png',
      'image/webp',
      'image/gif',
    ];
    if (!allowed.includes(file.type as MealAnalysisMediaType)) {
      setLocalError('Formato no soportado. Usa JPG, PNG, WEBP o GIF.');
      return;
    }
    // 5 MB raw (≈ 6.7 MB base64) — limita ANTES del FileReader.
    if (file.size > 5 * 1024 * 1024) {
      setLocalError('La imagen no puede superar los 5 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') return;
      // result: 'data:image/jpeg;base64,XXXX'
      const commaIdx = result.indexOf(',');
      const b64 = commaIdx >= 0 ? result.slice(commaIdx + 1) : result;
      setBase64(b64);
      setMediaType(file.type as MealAnalysisMediaType);
      // El object URL es válido para <img src> sin convertir base64 → blob URL.
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(file));
    };
    reader.onerror = () => {
      setLocalError('No se pudo leer la imagen.');
    };
    reader.readAsDataURL(file);
  }

  async function handleAnalyze() {
    if (!base64 || !mediaType) return;
    if (mutation.isPending) return;
    try {
      const res = await mutation.mutateAsync({ imageBase64: base64, mediaType });
      onAnalyzed(res.analysis);
    } catch {
      // El error queda en mutation.error → renderizado debajo.
    }
  }

  return (
    <div className="space-y-3">
      <div className="rounded-md border-2 border-dashed border-slate-300 bg-slate-50 p-4 text-center">
        <label
          htmlFor={fileInputId}
          className="inline-block cursor-pointer rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus-within:ring-2 focus-within:ring-teal-600 focus-within:ring-offset-2"
        >
          Seleccionar foto
        </label>
        <input
          id={fileInputId}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileChange}
          className="sr-only"
        />
        <p className="mt-2 text-xs text-slate-600">
          En móvil se abrirá la cámara. JPG / PNG / WEBP / GIF · máx 5 MB.
        </p>
      </div>

      {previewUrl && (
        <div>
          <img
            src={previewUrl}
            alt="Vista previa de la comida"
            className="mx-auto max-h-64 rounded-md border border-slate-200 object-contain shadow-sm"
          />
        </div>
      )}

      {localError && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {localError}
        </p>
      )}

      {mutation.isError && (
        <PhotoAnalysisError error={mutation.error} />
      )}

      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={handleAnalyze}
          disabled={!base64 || mutation.isPending}
          aria-busy={mutation.isPending}
          className="rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? 'Analizando con IA…' : 'Analizar'}
        </button>
      </div>
    </div>
  );
}

function PhotoAnalysisError({ error }: { error: Error | null }): ReactElement {
  if (error instanceof MealAnalysisVisionUnavailableError) {
    return (
      <p role="alert" className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
        {error.message} Configúrala en <strong>Ajustes → Anthropic</strong> o usa la pestaña de texto.
      </p>
    );
  }
  if (error instanceof MealAnalysisInvalidError) {
    return (
      <p role="alert" className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
        {error.message}
      </p>
    );
  }
  return (
    <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
      No se pudo analizar la foto. {error?.message ?? ''}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Text capture
// ---------------------------------------------------------------------------

function TextCapture({
  onAnalyzed,
}: {
  onAnalyzed: (a: MealAnalysis) => void;
}): ReactElement {
  const mutation = useAnalyzeText();
  const [text, setText] = useState('');
  const textareaId = useId();

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (mutation.isPending) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    try {
      const res = await mutation.mutateAsync({ text: trimmed });
      onAnalyzed(res.analysis);
    } catch {
      // mutation.error queda disponible.
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3" noValidate>
      <div>
        <label htmlFor={textareaId} className="block text-sm font-medium text-slate-700">
          Describe la comida
        </label>
        <textarea
          id={textareaId}
          rows={4}
          maxLength={500}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Ej: un plato de pasta con tomate y atún, unos 200g de pasta cocida"
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        />
        <p className="mt-1 text-xs text-slate-500">{text.length}/500</p>
      </div>

      {mutation.isError && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {mutation.error instanceof MealAnalysisInvalidError
            ? mutation.error.message
            : `No se pudo analizar el texto. ${mutation.error?.message ?? ''}`}
        </p>
      )}

      <div className="flex items-center justify-end gap-3">
        <button
          type="submit"
          disabled={!text.trim() || mutation.isPending}
          aria-busy={mutation.isPending}
          className="rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? 'Analizando con IA…' : 'Analizar'}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Analysis review form
// ---------------------------------------------------------------------------

function AnalysisReview({
  analysis,
  onBack,
  onSaved,
  onCancel,
}: {
  analysis: MealAnalysis;
  onBack: () => void;
  onSaved: () => void;
  onCancel: () => void;
}): ReactElement {
  const mutation = useLogMeal();
  const [draft, setDraft] = useState<ReviewDraft>(() => analysisToDraft(analysis));
  const [formError, setFormError] = useState<string | null>(null);
  const itemsRef = useRef<HTMLDivElement>(null);

  const lowConfidence = analysis.confidence < 0.5;

  const itemsText = useMemo(() => draft.items.join(', '), [draft.items]);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (mutation.isPending) return;
    const desc = buildDescription(draft);
    if (!desc) {
      setFormError('Necesitas un título o lista de items.');
      return;
    }
    const body: LogMealRequest = {
      datetime: new Date(draft.datetimeLocal).toISOString(),
      mealType: draft.mealType,
      description: desc,
    };
    if (Number.isFinite(draft.kcal)) body.kcal = Math.round(draft.kcal);
    if (Number.isFinite(draft.protein_g)) body.protein_g = Math.round(draft.protein_g);
    if (Number.isFinite(draft.carbs_g)) body.carbs_g = Math.round(draft.carbs_g);
    if (Number.isFinite(draft.fat_g)) body.fat_g = Math.round(draft.fat_g);
    setFormError(null);
    mutation.mutate(body, { onSuccess: onSaved });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      {lowConfidence && (
        <p
          role="status"
          className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          ⚠️ La IA tiene baja confianza ({Math.round(analysis.confidence * 100)}%). Revisa
          y ajusta los valores antes de guardar.
        </p>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Fecha y hora" id="qa-datetime">
          <input
            id="qa-datetime"
            type="datetime-local"
            value={draft.datetimeLocal}
            onChange={(e) => setDraft({ ...draft, datetimeLocal: e.target.value })}
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
          />
        </Field>
        <Field label="Tipo de comida" id="qa-mealtype">
          <select
            id="qa-mealtype"
            value={draft.mealType}
            onChange={(e) => setDraft({ ...draft, mealType: e.target.value as MealType })}
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
          >
            {MEAL_TYPES.map((t) => (
              <option key={t} value={t}>
                {mealTypeLabel(t)}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="Título" id="qa-title">
        <input
          id="qa-title"
          type="text"
          maxLength={200}
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        />
      </Field>

      <Field label="Items identificados" id="qa-items">
        <textarea
          id="qa-items"
          ref={null}
          rows={2}
          value={itemsText}
          onChange={(e) =>
            setDraft({
              ...draft,
              items: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
            })
          }
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        />
        <p className="mt-1 text-xs text-slate-500">Separa con comas. Cantidades en gramos.</p>
      </Field>

      <div ref={itemsRef} className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Field label="Kcal" id="qa-kcal">
          <NumberInput
            id="qa-kcal"
            value={draft.kcal}
            onChange={(v) => setDraft({ ...draft, kcal: v })}
          />
        </Field>
        <Field label="Proteína (g)" id="qa-protein">
          <NumberInput
            id="qa-protein"
            value={draft.protein_g}
            onChange={(v) => setDraft({ ...draft, protein_g: v })}
          />
        </Field>
        <Field label="Carbos (g)" id="qa-carbs">
          <NumberInput
            id="qa-carbs"
            value={draft.carbs_g}
            onChange={(v) => setDraft({ ...draft, carbs_g: v })}
          />
        </Field>
        <Field label="Grasas (g)" id="qa-fat">
          <NumberInput
            id="qa-fat"
            value={draft.fat_g}
            onChange={(v) => setDraft({ ...draft, fat_g: v })}
          />
        </Field>
      </div>

      {formError && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {formError}
        </p>
      )}
      {mutation.isError && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          No se pudo guardar. {mutation.error?.message ?? ''}
        </p>
      )}

      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onBack}
          disabled={mutation.isPending}
          className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          ← Volver
        </button>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={mutation.isPending}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            aria-busy={mutation.isPending}
            className="rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {mutation.isPending ? 'Guardando…' : 'Guardar en log'}
          </button>
        </div>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Field & inputs
// ---------------------------------------------------------------------------

function Field({
  label,
  id,
  children,
}: {
  label: string;
  id: string;
  children: ReactNode;
}): ReactElement {
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-slate-700">
        {label}
      </label>
      {children}
    </div>
  );
}

function NumberInput({
  id,
  value,
  onChange,
}: {
  id: string;
  value: number;
  onChange: (v: number) => void;
}): ReactElement {
  return (
    <input
      id={id}
      type="number"
      min={0}
      inputMode="numeric"
      value={Number.isFinite(value) ? value : ''}
      onChange={(e) => {
        const v = e.target.value === '' ? Number.NaN : Number(e.target.value);
        onChange(v);
      }}
      className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
    />
  );
}

// ---------------------------------------------------------------------------
// Draft + helpers
// ---------------------------------------------------------------------------

interface ReviewDraft {
  datetimeLocal: string;
  mealType: MealType;
  title: string;
  items: string[];
  kcal: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
}

function analysisToDraft(a: MealAnalysis): ReviewDraft {
  return {
    datetimeLocal: nowLocalDatetime(),
    mealType: guessMealTypeForNow(),
    title: a.title,
    items: a.items.map((it) => `${it.name} (${Math.round(it.estimatedGrams)}g)`),
    kcal: Math.round(a.totalKcal),
    protein_g: Math.round(a.macros.protG),
    carbs_g: Math.round(a.macros.choG),
    fat_g: Math.round(a.macros.fatG),
  };
}

function buildDescription(draft: ReviewDraft): string {
  const t = draft.title.trim();
  const items = draft.items.join(', ');
  if (t && items) return `${t} — ${items}`.slice(0, 500);
  if (t) return t.slice(0, 500);
  return items.slice(0, 500);
}

function nowLocalDatetime(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

function guessMealTypeForNow(): MealType {
  const h = new Date().getHours();
  if (h < 11) return 'breakfast';
  if (h < 16) return 'lunch';
  if (h < 19) return 'snack';
  return 'dinner';
}

// ---------------------------------------------------------------------------
// Modal (mismo patrón que MealLogView)
// ---------------------------------------------------------------------------

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}): ReactElement {
  const titleId = useId();

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 py-6"
      onKeyDown={handleKeyDown}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl"
      >
        <header className="mb-4 flex items-start justify-between gap-4 border-b border-slate-200 pb-3">
          <h2 id={titleId} className="text-lg font-semibold text-slate-900">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-600"
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}
